import { useEffect, useMemo, useReducer, useRef } from "react";
import { fingerprintVendorPayment, randomUuidKey, VendorPaymentPayload } from "./vendorPaymentIdempotency";

export type VendorPaymentBody = VendorPaymentPayload & { company_id: number };
export type SavedVendorPayment = {
  version: 1;
  companyId: number;
  billId: number;
  state: "in-flight" | "ambiguous";
  idempotencyKey: string;
  submittedFingerprint: string;
  submittedPayload: VendorPaymentBody;
};
type Slot = { ticket: SavedVendorPayment | null; problem: "storage" | "corrupt" | null };
export const vendorPaymentStorageKey = (companyId: number, billId: number) =>
  `ricebook_vendor_payment_attempt_v1:${companyId}:${billId}`;

// Shape and identity only. Do not revalidate dates, amounts, account availability,
// balance or posting rules: a previously accepted body must remain replayable.
export function hydrateVendorPayment(raw: unknown, companyId: number, billId: number): SavedVendorPayment | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const a = raw as SavedVendorPayment;
  const p = a.submittedPayload;
  if (a.version !== 1 || a.companyId !== companyId || a.billId !== billId ||
      !["in-flight", "ambiguous"].includes(a.state) ||
      typeof a.idempotencyKey !== "string" || !a.idempotencyKey ||
      typeof a.submittedFingerprint !== "string" ||
      !p || typeof p !== "object" || Array.isArray(p) ||
      p.company_id !== companyId || p.bill_id !== billId ||
      Object.prototype.hasOwnProperty.call(p, "idempotency_key") ||
      typeof p.vendor_payment_date !== "string" || typeof p.amount !== "string" ||
      typeof p.payment_account_id !== "number" ||
      fingerprintVendorPayment(p) !== a.submittedFingerprint) return null;
  return { ...a, state: "ambiguous" };
}

export function readVendorPayment(companyId: number, billId: number): Slot {
  let raw: string | null;
  try { raw = localStorage.getItem(vendorPaymentStorageKey(companyId, billId)); }
  catch { return { ticket: null, problem: "storage" }; }
  if (raw === null) return { ticket: null, problem: null };
  try {
    const ticket = hydrateVendorPayment(JSON.parse(raw), companyId, billId);
    return { ticket, problem: ticket ? null : "corrupt" };
  } catch { return { ticket: null, problem: "corrupt" }; }
}

type Context = Slot & {
  companyId: number; billId: number; pending: boolean; voided: boolean;
};

// All asynchronous work captures a context and an attempt. A response for A may
// resolve A's slot, but can never update the currently displayed B or its slot.
export function useVendorPaymentRecovery(companyId: number | null, billId: number | null,
  voided: boolean, restoreFields: (body: VendorPaymentBody) => void) {
  const [, render] = useReducer(n => n + 1, 0);
  const restoreRef = useRef(restoreFields);
  restoreRef.current = restoreFields;
  const context = useMemo<Context | null>(() => companyId !== null && billId !== null
    ? { companyId, billId, ...readVendorPayment(companyId, billId), pending: false, voided: false }
    : null, [companyId, billId]);
  const current = useRef(context);
  current.current = context;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const update = (ctx: Context) => {
    if (mounted.current && current.current === ctx) render();
  };

  useEffect(() => {
    if (!context) return;
    context.voided = voided;
    if (voided) {
      // This flag comes only from the loaded server document. Clear only the
      // exact valid ticket we read, never an unrelated replacement.
      const slot = readVendorPayment(context.companyId, context.billId);
      if (slot.ticket) {
        try {
          const latest = readVendorPayment(context.companyId, context.billId);
          if (!latest.problem && latest.ticket?.idempotencyKey === slot.ticket.idempotencyKey) {
            localStorage.removeItem(vendorPaymentStorageKey(context.companyId, context.billId));
          }
        } catch { /* No payment actions on this voided document, even if cleanup fails. */ }
      }
      context.ticket = null;
      context.pending = false;
    } else if (context.ticket) restoreRef.current(context.ticket.submittedPayload);
    render();
  }, [context, voided]);

  function begin(body: VendorPaymentBody) {
    const ctx = context;
    if (!ctx || voided || ctx.voided || ctx.pending) return null;
    // Re-read immediately before sending. No cross-tab lock is claimed.
    const slot = readVendorPayment(ctx.companyId, ctx.billId);
    if (slot.problem) { Object.assign(ctx, slot); update(ctx); return null; }
    const saved = slot.ticket || ctx.ticket;
    if (saved && saved.submittedFingerprint !== fingerprintVendorPayment(body)) {
      ctx.ticket = saved; ctx.problem = null; update(ctx); return null;
    }
    const attempt: SavedVendorPayment = saved ? { ...saved, state: "in-flight" } : {
      version: 1, companyId: ctx.companyId, billId: ctx.billId, state: "in-flight",
      idempotencyKey: randomUuidKey(), submittedFingerprint: fingerprintVendorPayment(body),
      submittedPayload: body,
    };
    try { localStorage.setItem(vendorPaymentStorageKey(ctx.companyId, ctx.billId), JSON.stringify(attempt)); }
    catch { ctx.problem = "storage"; update(ctx); return null; }
    ctx.ticket = attempt; ctx.problem = null; ctx.pending = true; update(ctx);
    let completed = false;
    const isCurrent = () => mounted.current && current.current === ctx && !ctx.voided;
    return {
      attempt, isCurrent,
      finish(keepPending = false) {
        // Terminal BEFORE clearing; delayed callbacks cannot save this attempt.
        completed = true; ctx.pending = keepPending;
        const latest = readVendorPayment(ctx.companyId, ctx.billId);
        if (!latest.problem && latest.ticket?.idempotencyKey === attempt.idempotencyKey) {
          try {
            localStorage.removeItem(vendorPaymentStorageKey(ctx.companyId, ctx.billId));
            ctx.ticket = null; ctx.problem = null;
          } catch { ctx.ticket = null; ctx.problem = "storage"; }
        } else Object.assign(ctx, latest);
        update(ctx);
      },
      release() { ctx.pending = false; update(ctx); },
      uncertain() {
        if (completed || ctx.voided) return;
        ctx.pending = false;
        const latest = readVendorPayment(ctx.companyId, ctx.billId);
        if (!latest.problem && (!latest.ticket || latest.ticket.idempotencyKey === attempt.idempotencyKey)) {
          ctx.ticket = { ...attempt, state: "ambiguous" };
          try { localStorage.setItem(vendorPaymentStorageKey(ctx.companyId, ctx.billId), JSON.stringify(ctx.ticket)); }
          catch { /* The already saved in-flight body restores as ambiguous. */ }
        } else Object.assign(ctx, latest);
        update(ctx);
      },
    };
  }

  return {
    ticket: context?.ticket ?? null,
    problem: context?.problem ?? null,
    pending: context?.pending ?? false,
    exact: (body: VendorPaymentBody) => !!context?.ticket &&
      context.ticket.submittedFingerprint === fingerprintVendorPayment(body),
    restore: () => { if (context?.ticket) restoreRef.current(context.ticket.submittedPayload); },
    startNew: () => {
      if (!context || context.pending || voided || context.voided) return;
      // A different tab may have replaced the corrupt record with a valid
      // attempt. Never erase that newer key through the old recovery button.
      const latest = readVendorPayment(context.companyId, context.billId);
      if (latest.problem !== "corrupt") { Object.assign(context, latest); update(context); return; }
      // Explicitly authorized corrupt-ticket disposal. No automatic reset.
      try {
        localStorage.removeItem(vendorPaymentStorageKey(context.companyId, context.billId));
        context.ticket = null; context.problem = null;
      } catch { context.problem = "corrupt"; }
      update(context);
    },
    begin,
  };
}
